import React, { useMemo } from 'react'
import styled from 'styled-components'
import { matchPath, useRouteMatch } from 'react-router'
import { useAppSelector } from 'hooks/reduxHooks'
import SiteTakeoverBackgroundImage from './SiteTakeoverBackgroundImage'
import useCurrentSiteTakeover from 'hooks/useCurrentSiteTakeover'
import { themeClassName } from 'lib/theme/themeUtils'
import useOffer from 'hooks/Offers/useOffer'
import PageBanner from 'components/Luxkit/Banners/PageBanner'

const Background = styled(SiteTakeoverBackgroundImage)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
`

const Content = styled.div`
  transform: translateZ(0);
`

const Banner = styled.div`
  position: relative;
`

const offerPageRoutes: Array<string> = [
  '/:regionCode/offer/:slug/:offerId',
  '/:regionCode/cruises/:slug/:offerId',
  '/:regionCode/tour/:offerId',
]

const noBannerRoutes: Array<string> = [
  '/:regionCode/search',
  '/:regionCode/checkout',
]

const toursRoutes: Array<string> = [
  '/:regionCode/tours',
  '/:regionCode/tour/:offerId',
]

const invalidExactRoutes: Array<string> = [
  '/:regionCode',
  '/:regionCode/triple-points',
]

function SiteTakeoverPageBanner() {
  const match = useRouteMatch<{offerId: string}>(offerPageRoutes)
  const [offer] = useOffer(match?.params.offerId)
  const currentPath = useAppSelector(state => state.router.location.pathname)
  const siteTakeover = useCurrentSiteTakeover()
  const siteTakeoverOnTour = useCurrentSiteTakeover({ vertical: 'tours' })
  const showingOfferBanner = !!(offer && siteTakeover?.offerTypes?.has(offer.type))
  const hide = useMemo((): boolean => {
    // hide on:
    // - The home page (has the carousel)
    // - Search page results (has it's own banner)
    // - Would show the offer page banner  (has it's own banner)
    // - Any of the other no banner routes (shouldn't show banners)
    // - When tour has it's own takeover
    return showingOfferBanner ||
      !!matchPath(currentPath, { path: invalidExactRoutes, exact: true }) ||
      !!matchPath(currentPath, { path: noBannerRoutes }) ||
      (!!siteTakeoverOnTour?.verticals.has('tours') && !!matchPath(currentPath, { path: toursRoutes }))
  }, [currentPath, showingOfferBanner, siteTakeoverOnTour])

  if (!siteTakeover?.landingPage?.banner || hide) {
    return null
  }

  return <Banner>
    <Background background="default"/>
    <Content className={themeClassName(siteTakeover.palette)}>
      <PageBanner
        description={siteTakeover.landingPage.banner.description}
        primaryActionButtonProps={{
          to: siteTakeover.landingPage.banner.bannerAction?.to,
          children: siteTakeover.landingPage.banner.bannerAction?.label,
        }}
        density="compact"
        icon={siteTakeover.landingPage.banner.logo}
        align="center"
        kind="none"
      />
    </Content>
  </Banner>
}

export default SiteTakeoverPageBanner
